"use client";

// Browser data store for the invoice tool.
// - Saved to IndexedDB (large quota, persistent storage requested), with a localStorage fallback.
// - Deletes go to a recycle bin for 30 days and can be restored.
// - Optional auto-backup: every change is also written to a JSON file you pick (Chrome / Edge).

import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { DEFAULT_BRAND, DEFAULT_SETTINGS, buildSampleData } from "./defaults";
import { idbGet, idbSet, idbDel, requestPersistence } from "./idb";
import { computeTotals, counterKey, sortLines, uid } from "./utils";
import { decryptJSON, encryptJSON, exportKeyB64, importKeyB64, lockedCopy, makeUser, newDataKey, unlockWith } from "./crypto";

const SESSION_KEY = "invoice-studio-session";
const saveSession = async (key, username) => {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ k: await exportKeyB64(key), u: username }));
  } catch {
    /* ignore */
  }
};
const clearSession = () => {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
};

const AUTH_KEY = "invoice-studio-auth"; // public metadata: user IDs + wrapped keys (no secrets)
const readAuth = () => {
  try {
    const a = JSON.parse(localStorage.getItem(AUTH_KEY) || "null");
    return a?.users?.length ? a : null;
  } catch {
    return null;
  }
};
const writeAuth = (a) => {
  try {
    if (a) localStorage.setItem(AUTH_KEY, JSON.stringify(a));
    else localStorage.removeItem(AUTH_KEY);
  } catch {
    /* ignore */
  }
};

const LEGACY_KEY = "alphabet-invoice-tool-v1";
const TRASH_DAYS = 30;

const EMPTY = { settings: DEFAULT_SETTINGS, brands: [DEFAULT_BRAND], clients: [], projects: [], invoices: [], sales: [], trash: [] };

function mergeSettings(saved = {}) {
  const out = { ...DEFAULT_SETTINGS, ...saved };
  for (const k of ["numbering", "defaults", "backup"]) out[k] = { ...DEFAULT_SETTINGS[k], ...(saved[k] || {}) };
  out.tags = Array.isArray(out.tags) ? out.tags.filter((t) => t && typeof t === "object" && t.id).map((t) => ({ ...t, name: String(t.name ?? t.id), color: t.color || "#64748b" })) : [];
  if (!out.tags.length) out.tags = DEFAULT_SETTINGS.tags;
  if (out.ai && typeof out.ai !== "object") out.ai = {};
  if (typeof out.appName !== "string") out.appName = "";
  if (typeof out.ownerName !== "string") out.ownerName = "";
  delete out.company;
  delete out.bank;
  delete out.bankInr;
  return out;
}

const mergeBrand = (b) => ({
  ...DEFAULT_BRAND,
  ...b,
  bank: { ...DEFAULT_BRAND.bank, ...(b?.bank || {}) },
  bankInr: { ...DEFAULT_BRAND.bankInr, ...(b?.bankInr || {}) },
});

// Repair records from older versions, other devices or imports so a single odd value
// (missing name, number where text is expected, empty item) can never crash a page.
const list = (a) => (Array.isArray(a) ? a.filter((x) => x && typeof x === "object") : []);
const text = (v) => (v == null ? "" : typeof v === "object" ? "" : String(v));
const TEXT_FIELDS = ["name", "contactPerson", "email", "ccEmail", "phone", "whatsapp", "address", "area", "city", "state", "postcode", "country", "gstin", "pan", "currency", "taxMode", "notes"];
function cleanClient(c) {
  const out = { ...c, id: c.id || uid() };
  for (const k of TEXT_FIELDS) if (k in out || k === "name") out[k] = text(out[k]);
  return out;
}
function cleanProject(p) {
  return { ...p, id: p.id || uid(), name: text(p.name), service: text(p.service), clientId: text(p.clientId), status: p.status || "active", billing: p.billing || "monthly" };
}
// House rule: invoice lines are always in A–Z order (see sortLines).
const az = (inv) => ({ ...inv, items: sortLines(inv.items || []) });

function cleanInvoice(i) {
  return {
    ...i,
    id: i.id || uid(),
    number: text(i.number),
    date: text(i.date),
    dueDate: text(i.dueDate),
    currency: text(i.currency) || "GBP",
    items: sortLines(list(i.items).map((l) => ({ ...l, id: l.id || uid(), description: text(l.description) }))),
    payments: list(i.payments),
    billTo: i.billTo && typeof i.billTo === "object" ? Object.fromEntries(Object.entries(i.billTo).map(([k, v]) => [k, text(v)])) : {},
  };
}

function normalise(data = {}) {
  let brands = Array.isArray(data.brands) && data.brands.length ? data.brands : null;
  if (!brands) {
    // migrate v1 (single company in settings) to brand profiles
    const s = data.settings || {};
    brands = [
      s.company
        ? { ...s.company, id: "default", bank: s.bank, bankInr: s.bankInr, accent: s.defaults?.accent || DEFAULT_BRAND.accent, template: "classic" }
        : DEFAULT_BRAND,
    ];
  }
  const cutoff = Date.now() - TRASH_DAYS * 864e5;
  return {
    settings: mergeSettings(data.settings),
    _savedAt: data._savedAt || "",
    brands: (brands.filter((b) => b && typeof b === "object").length ? brands.filter((b) => b && typeof b === "object") : [DEFAULT_BRAND]).map(mergeBrand),
    clients: list(data.clients).map(cleanClient),
    projects: list(data.projects).map(cleanProject),
    invoices: list(data.invoices).map(cleanInvoice),
    sales: list(data.sales).map((x) => ({ ...x, id: x.id || uid(), description: text(x.description) })),
    trash: (Array.isArray(data.trash) ? data.trash : []).filter((t) => new Date(t.deletedAt).getTime() > cutoff),
  };
}

// A finalised credit note raised against an invoice is applied to that invoice as an
// adjustment (not cash), so its balance, status and aging stay correct.
function syncCreditNote(d, cn) {
  const target = d.invoices.find((i) => i.id === cn.againstId);
  if (!target) return d;
  const others = (target.payments || []).filter((p) => p.creditNoteId !== cn.id);
  const active = !["draft", "cancelled"].includes(cn.status);
  const payments = active ? [...others, { id: `cn-${cn.id}`, creditNoteId: cn.id, date: cn.date, amount: computeTotals(cn).total, method: "Credit note", ref: cn.number }] : others;
  return { ...d, invoices: d.invoices.map((i) => (i.id === target.id ? { ...i, payments } : i)) };
}

// Sales entries are "billed" by the tax invoice whose lines reference them (line.saleId).
// Called after every invoice save / cancel / delete so a sale is never billed twice or lost.
function syncSales(d, inv, removed = false) {
  const active = !removed && (inv.docType || "invoice") === "invoice" && inv.status !== "cancelled";
  const linked = new Set(active ? (inv.items || []).map((l) => l.saleId).filter(Boolean) : []);
  let changed = false;
  const sales = (d.sales || []).map((x) => {
    if (linked.has(x.id) && (x.invoiceId !== inv.id || x.invoiceNumber !== inv.number)) {
      changed = true;
      return { ...x, invoiceId: inv.id, invoiceNumber: inv.number, billedAt: new Date().toISOString() };
    }
    if (!linked.has(x.id) && x.invoiceId === inv.id) {
      changed = true;
      return { ...x, invoiceId: "", invoiceNumber: "", billedAt: "" };
    }
    return x;
  });
  return changed ? { ...d, sales } : d;
}

const StoreContext = createContext(null);
const now = () => new Date().toISOString();
const supportsFileBackup = () => typeof window !== "undefined" && "showSaveFilePicker" in window;

export function InvoiceStoreProvider({ children }) {
  const [data, setData] = useState(EMPTY);
  const [ready, setReady] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [persisted, setPersisted] = useState(false);
  const [backup, setBackup] = useState({ supported: false, linked: false, granted: false, name: "", lastWrite: "", error: "" });
  const handleRef = useRef(null);
  const skipFirstSave = useRef(true);
  // ----- login / encryption state -----
  const [auth, setAuth] = useState(null); // public auth metadata when login is on
  const [locked, setLocked] = useState(false);
  const [currentUser, setCurrentUser] = useState("");
  const keyRef = useRef(null); // AES data key while unlocked
  const saveSeq = useRef(0);

  // ---------- load ----------
  const readStored = useCallback(async () => {
    let raw = null;
    try {
      raw = await idbGet("data");
    } catch {
      /* IndexedDB blocked – fall back below */
    }
    // The localStorage mirror wins if it is newer (e.g. tab closed mid-write).
    try {
      const ls = localStorage.getItem(LEGACY_KEY);
      const mirror = ls ? JSON.parse(ls) : null;
      if (mirror && (!raw || (mirror._savedAt || "") > (raw._savedAt || ""))) raw = mirror;
    } catch {
      /* ignore */
    }
    return raw;
  }, []);

  // Decrypt (if needed) and show the stored data.
  const openData = useCallback(
    async (key) => {
      const raw = await readStored();
      let obj = raw;
      if (raw?.encrypted) {
        if (!key) throw new Error("locked");
        obj = await decryptJSON(key, raw);
      }
      skipFirstSave.current = !(auth || readAuth()) || !!raw?.encrypted; // re-save once to encrypt old plain data
      setData(obj ? normalise(obj) : EMPTY);
      setLocked(false);
      setReady(true);
    },
    [readStored, auth]
  );

  useEffect(() => {
    let alive = true;
    (async () => {
      const a = readAuth();
      setAuth(a);
      let opened = false;
      if (a) {
        try {
          let remembered = await idbGet("authKey");
          if (!remembered?.key) {
            const sess = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "null");
            if (sess?.k) remembered = { key: await importKeyB64(sess.k), username: sess.u };
          }
          if (remembered?.key) {
            keyRef.current = remembered.key;
            setCurrentUser(remembered.username || "");
            await openData(remembered.key);
            opened = true;
          }
        } catch {
          keyRef.current = null;
        }
        if (!opened) {
          if (alive) setLocked(true);
        }
      } else {
        const raw = await readStored();
        if (!alive) return;
        if (raw?.encrypted) {
          // encrypted data but no local login metadata: use the copy embedded in the data
          if (raw.auth?.users?.length) {
            writeAuth(raw.auth);
            setAuth(raw.auth);
          }
          setLocked(true);
        } else {
          if (raw) setData(normalise(raw));
          setReady(true);
        }
      }
      setPersisted(await requestPersistence());
      const sup = supportsFileBackup();
      let handle = null;
      try {
        handle = sup ? await idbGet("backupHandle") : null;
      } catch {
        /* ignore */
      }
      handleRef.current = handle || null;
      let granted = false;
      if (handle) {
        try {
          granted = (await handle.queryPermission({ mode: "readwrite" })) === "granted";
        } catch {
          /* ignore */
        }
      }
      setBackup((b) => ({ ...b, supported: sup, linked: !!handle, granted, name: handle?.name || "" }));
    })();
    return () => {
      alive = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- save (immediately on every change) ----------
  const pending = useRef(0);
  useEffect(() => {
    if (!ready) return;
    if (skipFirstSave.current) {
      skipFirstSave.current = false;
      return;
    }
    const stamped = { ...data, _savedAt: now() };
    const seq = ++saveSeq.current;
    const key = keyRef.current;
    const authMeta = auth;
    pending.current++;
    (async () => {
      // with login on, everything at rest is encrypted
      const payload = authMeta && key ? await encryptJSON(key, stamped, { _savedAt: stamped._savedAt, auth: authMeta }) : stamped;
      if (authMeta && !key) return; // never write plain data while login is on
      if (seq !== saveSeq.current) return; // a newer save superseded this one
      // 1) quick localStorage mirror (survives the tab closing mid-write; skipped once data outgrows ~5 MB)
      let mirrored = false;
      try {
        localStorage.setItem(LEGACY_KEY, JSON.stringify(payload));
        mirrored = true;
      } catch {
        try {
          localStorage.removeItem(LEGACY_KEY);
        } catch {
          /* ignore */
        }
      }
      // 2) main store: IndexedDB (large quota)
      try {
        await idbSet("data", payload);
        setSaveError("");
      } catch {
        if (!mirrored) setSaveError("Could not save your data in this browser. Export a backup now (Settings → Data).");
      }
    })()
      .catch(() => setSaveError("Could not encrypt/save your data. Export a backup now."))
      .finally(() => {
        pending.current--;
      });
  }, [data, ready]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ask the browser to wait if a save is still being written when the tab closes.
  useEffect(() => {
    const h = (e) => {
      if (pending.current > 0) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, []);

  // ---------- auto-backup to linked file ----------
  const writeBackupFile = useCallback(async (payload) => {
    const h = handleRef.current;
    if (!h) return false;
    try {
      if ((await h.queryPermission({ mode: "readwrite" })) !== "granted") {
        setBackup((b) => ({ ...b, granted: false }));
        return false;
      }
      const w = await h.createWritable();
      await w.write(JSON.stringify(payload, null, 1));
      await w.close();
      setBackup((b) => ({ ...b, granted: true, lastWrite: now(), error: "" }));
      return true;
    } catch (e) {
      setBackup((b) => ({ ...b, error: e.message }));
      return false;
    }
  }, []);

  const exportPayload = useCallback((d) => ({ ...d, exportedAt: now(), app: "invoice-studio", version: 2 }), []);
  // Encrypt anything leaving the browser (Drive file, backups) when login is on.
  const protect = useCallback(async (obj) => (auth && keyRef.current ? encryptJSON(keyRef.current, obj, { auth, exportedAt: now() }) : obj), [auth]);

  useEffect(() => {
    if (!ready || !backup.linked || !backup.granted) return;
    const t = setTimeout(async () => writeBackupFile(await protect(exportPayload(data))), 2500);
    return () => clearTimeout(t);
  }, [data, ready, backup.linked, backup.granted, writeBackupFile, exportPayload, protect]);

  // ---------- helpers ----------
  const upsert = useCallback((collection, item) => {
    const record = { ...item, id: item.id || uid(), updatedAt: now(), createdAt: item.createdAt || now() };
    setData((d) => {
      const exists = d[collection].some((x) => x.id === record.id);
      return {
        ...d,
        [collection]: exists ? d[collection].map((x) => (x.id === record.id ? record : x)) : [record, ...d[collection]],
      };
    });
    return record;
  }, []);

  const toTrash = (d, entry) => ({ ...d, trash: [{ id: entry.id || uid(), deletedAt: now(), ...entry }, ...d.trash] });

  const api = useMemo(() => {
    const brandById = (id) => data.brands.find((b) => b.id === id) || data.brands.find((b) => b.id === data.settings.defaultBrandId) || data.brands[0];
    return {
      ...data,
      ready,
      saveError,
      persisted,
      backup,
      brandById,
      defaultBrand: brandById(data.settings.defaultBrandId),

      // brands
      saveBrand: (b) => upsert("brands", mergeBrand(b)),
      deleteBrand: (id) => {
        if (data.invoices.some((i) => i.brandId === id)) return "This profile is used by existing invoices, so it can’t be deleted.";
        if (data.brands.length <= 1) return "You need at least one business profile.";
        setData((d) => ({
          ...d,
          brands: d.brands.filter((b) => b.id !== id),
          settings: d.settings.defaultBrandId === id ? { ...d.settings, defaultBrandId: d.brands.find((b) => b.id !== id).id } : d.settings,
        }));
        return "";
      },

      // clients
      saveClient: (c) => upsert("clients", c),
      setClientArchived: (id, archived) =>
        setData((d) => ({ ...d, clients: d.clients.map((c) => (c.id === id ? { ...c, archived, updatedAt: now() } : c)) })),
      deleteClient: (id) => {
        const tid = uid();
        setData((d) => {
          const client = d.clients.find((c) => c.id === id);
          if (!client) return d;
          const projects = d.projects.filter((p) => p.clientId === id);
          return toTrash(
            { ...d, clients: d.clients.filter((c) => c.id !== id), projects: d.projects.filter((p) => p.clientId !== id) },
            { id: tid, kind: "client", label: client.name, item: client, related: projects }
          );
        });
        return tid;
      },

      // projects
      saveProject: (p) => upsert("projects", p),
      saveProjects: (list) =>
        setData((d) => {
          const byId = new Map(d.projects.map((p) => [p.id, p]));
          for (const p of list) {
            const id = p.id || uid();
            byId.set(id, { ...p, id, createdAt: p.createdAt || now(), updatedAt: now() });
          }
          return { ...d, projects: [...byId.values()] };
        }),
      deleteProjects: (ids) => {
        const tid = uid();
        setData((d) => {
          const set = new Set(ids);
          const gone = d.projects.filter((p) => set.has(p.id));
          if (!gone.length) return d;
          return toTrash(
            { ...d, projects: d.projects.filter((p) => !set.has(p.id)) },
            { id: tid, kind: "projects", label: gone.length === 1 ? gone[0].name : `${gone.length} projects`, item: null, related: gone }
          );
        });
        return tid;
      },

      // invoices
      saveInvoice: (inv, { seqUsed, action } = {}) => {
        const entry = { at: now(), action: action || (inv.id ? "Edited" : "Created") };
        const rec = upsert("invoices", { ...az(inv), history: [...(inv.history || []), entry].slice(-50) });
        if (rec.docType === "credit" && rec.againstId) setData((d) => syncCreditNote(d, rec));
        setData((d) => syncSales(d, rec));
        if (seqUsed) {
          setData((d) => {
            const key = counterKey(d.settings, inv.docType, inv.date, brandById(inv.brandId));
            const cur = Number(d.settings.numbering.counters?.[key]) || 0;
            if (seqUsed + 1 <= cur) return d;
            return {
              ...d,
              settings: { ...d.settings, numbering: { ...d.settings.numbering, counters: { ...d.settings.numbering.counters, [key]: seqUsed + 1 } } },
            };
          });
        }
        return rec;
      },
      saveInvoices: (list, counterUpdates = {}) =>
        setData((d) => {
          const created = list.map((i) => ({ ...az(i), id: i.id || uid(), createdAt: now(), updatedAt: now(), history: [{ at: now(), action: "Created by monthly billing run" }] }));
          let next = {
            ...d,
            invoices: [...created, ...d.invoices],
            settings: { ...d.settings, numbering: { ...d.settings.numbering, counters: { ...d.settings.numbering.counters, ...counterUpdates } } },
          };
          for (const inv of created) next = syncSales(next, inv);
          return next;
        }),
      deleteInvoice: (id) => {
        const tid = uid();
        setData((d) => {
          const inv = d.invoices.find((i) => i.id === id);
          if (!inv) return d;
          const cleaned = syncSales(inv.docType === "credit" && inv.againstId ? syncCreditNote(d, { ...inv, status: "cancelled" }) : d, inv, true);
          return toTrash({ ...cleaned, invoices: cleaned.invoices.filter((i) => i.id !== id) }, { id: tid, kind: "invoice", label: `${inv.number} – ${inv.billTo?.name || ""}`, item: inv });
        });
        return tid;
      },

      // sales log (work / one-off charges recorded during the month, invoiced at month end)
      saveSale: (x) => upsert("sales", x),
      saveSales: (list) =>
        setData((d) => {
          const byId = new Map(d.sales.map((x) => [x.id, x]));
          for (const x of list) {
            const id = x.id || uid();
            byId.set(id, { ...byId.get(id), ...x, id, createdAt: x.createdAt || byId.get(id)?.createdAt || now(), updatedAt: now() });
          }
          return { ...d, sales: [...byId.values()] };
        }),
      deleteSales: (ids) => {
        const tid = uid();
        setData((d) => {
          const set = new Set(ids);
          const gone = d.sales.filter((x) => set.has(x.id));
          if (!gone.length) return d;
          return toTrash({ ...d, sales: d.sales.filter((x) => !set.has(x.id)) }, { id: tid, kind: "sales", label: gone.length === 1 ? gone[0].description : `${gone.length} sales entries`, item: null, related: gone });
        });
        return tid;
      },

      // bulk import of past invoices (with any new clients / projects)
      importBatch: ({ clients = [], projects = [], invoices = [], sales = [], counters = {} }) =>
        setData((d) => {
          const merged = { ...d.settings.numbering.counters };
          for (const [k, v] of Object.entries(counters)) merged[k] = Math.max(Number(merged[k]) || 0, v);
          return {
            ...d,
            clients: [...clients.map((c) => ({ ...c, createdAt: now(), updatedAt: now() })), ...d.clients],
            projects: [...projects.map((p) => ({ ...p, createdAt: now(), updatedAt: now() })), ...d.projects],
            invoices: [...invoices.map((i) => ({ ...az(i), createdAt: now(), updatedAt: now() })), ...d.invoices],
            sales: [...sales.map((x) => ({ ...x, createdAt: now(), updatedAt: now() })), ...(d.sales || [])],
            settings: { ...d.settings, numbering: { ...d.settings.numbering, counters: merged } },
          };
        }),

      // remove everything one import created (invoices, and clients/projects nothing else uses)
      undoImport: (batch) =>
        setData((d) => {
          const invoices = d.invoices.filter((i) => i.imported?.batch !== batch);
          const sales = (d.sales || []).filter((x) => x.importBatch !== batch || x.invoiceId);
          const projectsLeft = d.projects.filter((p) => p.importBatch !== batch);
          const usedClients = new Set([...invoices.map((i) => i.clientId), ...sales.map((x) => x.clientId), ...projectsLeft.map((p) => p.clientId)]);
          const usedProjects = new Set([...invoices.flatMap((i) => (i.items || []).map((l) => l.projectId)), ...sales.map((x) => x.projectId)]);
          return {
            ...d,
            invoices,
            sales,
            clients: d.clients.filter((c) => c.importBatch !== batch || usedClients.has(c.id)),
            projects: d.projects.filter((p) => p.importBatch !== batch || usedProjects.has(p.id)),
          };
        }),
      // replace invoices in place (bulk edits such as "mark this import unpaid")
      updateInvoices: (list) =>
        setData((d) => {
          const byId = new Map(list.map((i) => [i.id, i]));
          return { ...d, invoices: d.invoices.map((i) => (byId.has(i.id) ? { ...az(byId.get(i.id)), updatedAt: now() } : i)) };
        }),

      // recycle bin
      restoreTrash: (trashId) =>
        setData((d) => {
          const t = d.trash.find((x) => x.id === trashId);
          if (!t) return d;
          const next = { ...d, trash: d.trash.filter((x) => x.id !== trashId) };
          const add = (coll, items) => {
            const ids = new Set(next[coll].map((x) => x.id));
            next[coll] = [...items.filter((x) => !ids.has(x.id)), ...next[coll]];
          };
          if (t.kind === "client") {
            add("clients", [t.item]);
            add("projects", t.related || []);
          } else if (t.kind === "projects") add("projects", t.related || []);
          else if (t.kind === "sales") add("sales", t.related || []);
          else if (t.kind === "invoice") {
            add("invoices", [t.item]);
            const withSales = syncSales(next, t.item);
            if (withSales !== next) Object.assign(next, withSales);
            if (t.item.docType === "credit" && t.item.againstId) return syncCreditNote(next, t.item);
          }
          return next;
        }),
      purgeTrash: (trashId) => setData((d) => ({ ...d, trash: trashId ? d.trash.filter((x) => x.id !== trashId) : [] })),

      // settings & data
      updateSettings: (patch) => setData((d) => ({ ...d, settings: mergeSettings({ ...d.settings, ...patch }) })),
      importAll: (json) => setData(normalise(json)),

      // ----- login / encryption -----
      auth,
      locked,
      currentUser,
      loginEnabled: !!auth,
      protect,
      exportBackup: async () => {
        setData((d) => ({ ...d, settings: { ...d.settings, backup: { ...d.settings.backup, lastExportAt: now() } } }));
        return protect(exportPayload(data));
      },
      // Decrypt an envelope (Drive file / backup). Throws Error("NEEDS_LOGIN") if this device can't open it.
      openEnvelope: async (env) => {
        if (!env?.encrypted) return env;
        if (keyRef.current) {
          try {
            return await decryptJSON(keyRef.current, env);
          } catch {
            /* different key – fall through */
          }
        }
        const e = new Error("NEEDS_LOGIN");
        e.code = "NEEDS_LOGIN";
        throw e;
      },
      // Sign in with the logins stored inside an envelope (new device / restored backup) and adopt them.
      openEnvelopeWithLogin: async (env, username, password) => {
        const { key, user } = await unlockWith(env.auth, username, password);
        const obj = await decryptJSON(key, env);
        keyRef.current = key;
        writeAuth(env.auth);
        setAuth(env.auth);
        setCurrentUser(user.username);
        await saveSession(key, user.username);
        return obj;
      },
      unlock: async (username, password, remember) => {
        const a = auth || readAuth();
        const { key, user } = await unlockWith(a, username, password);
        keyRef.current = key;
        setCurrentUser(user.username);
        if (remember) await idbSet("authKey", { key: await lockedCopy(key), username: user.username }).catch(() => {});
        await saveSession(key, user.username);
        await openData(key);
      },
      lock: async () => {
        keyRef.current = null;
        clearSession();
        await idbDel("authKey").catch(() => {});
        skipFirstSave.current = true;
        setReady(false);
        setData(EMPTY);
        setCurrentUser("");
        setLocked(true);
      },
      enableLogin: async (username, password) => {
        const key = await newDataKey();
        const a = { v: 1, users: [await makeUser(key, username, password)], autoLockMinutes: 0, company: data.brands?.[0]?.name || "", createdAt: now() };
        keyRef.current = key;
        writeAuth(a);
        setAuth(a);
        setCurrentUser(a.users[0].username);
        await saveSession(key, a.users[0].username);
        setData((d) => ({ ...d })); // re-save → now encrypted
      },
      addUser: async (me, myPassword, username, password) => {
        const { key } = await unlockWith(auth, me, myPassword);
        if (auth.users.some((u) => u.username.toLowerCase() === username.trim().toLowerCase())) throw new Error("That user ID already exists");
        const a = { ...auth, users: [...auth.users, await makeUser(key, username, password)] };
        writeAuth(a);
        setAuth(a);
        setData((d) => ({ ...d }));
      },
      changePassword: async (username, oldPassword, newPassword) => {
        const { key, user } = await unlockWith(auth, username, oldPassword);
        const fresh = await makeUser(key, user.username, newPassword);
        const a = { ...auth, users: auth.users.map((u) => (u.username === user.username ? { ...fresh, createdAt: u.createdAt } : u)) };
        writeAuth(a);
        setAuth(a);
        setData((d) => ({ ...d }));
      },
      removeUser: async (me, myPassword, username) => {
        await unlockWith(auth, me, myPassword);
        if (auth.users.length <= 1) throw new Error("You can’t remove the last user – turn login off instead");
        const a = { ...auth, users: auth.users.filter((u) => u.username.toLowerCase() !== username.toLowerCase()) };
        writeAuth(a);
        setAuth(a);
        setData((d) => ({ ...d }));
      },
      setAutoLock: (minutes) => {
        const a = { ...auth, autoLockMinutes: Number(minutes) || 0 };
        writeAuth(a);
        setAuth(a);
      },
      disableLogin: async (username, password) => {
        await unlockWith(auth, username, password);
        keyRef.current = null;
        writeAuth(null);
        setAuth(null);
        setCurrentUser("");
        clearSession();
        await idbDel("authKey").catch(() => {});
        setData((d) => ({ ...d })); // re-save → plain again
      },
      exportAll: () => {
        setData((d) => ({ ...d, settings: { ...d.settings, backup: { ...d.settings.backup, lastExportAt: now() } } }));
        return exportPayload(data);
      },
      loadSample: () => {
        const { client, projects } = buildSampleData(uid);
        setData((d) => ({ ...d, clients: [client, ...d.clients], projects: [...projects, ...d.projects] }));
        return client;
      },
      resetAll: () => setData(EMPTY),

      // auto-backup file
      linkBackupFile: async () => {
        const handle = await window.showSaveFilePicker({
          suggestedName: "invoice-studio-autobackup.json",
          types: [{ description: "JSON backup", accept: { "application/json": [".json"] } }],
        });
        handleRef.current = handle;
        await idbSet("backupHandle", handle);
        setBackup((b) => ({ ...b, linked: true, granted: true, name: handle.name }));
        await writeBackupFile(exportPayload(data));
      },
      reconnectBackup: async () => {
        const h = handleRef.current;
        if (!h) return;
        const granted = (await h.requestPermission({ mode: "readwrite" })) === "granted";
        setBackup((b) => ({ ...b, granted }));
        if (granted) await writeBackupFile(exportPayload(data));
      },
      unlinkBackup: async () => {
        handleRef.current = null;
        await idbDel("backupHandle");
        setBackup((b) => ({ ...b, linked: false, granted: false, name: "", lastWrite: "" }));
      },
    };
  }, [data, ready, saveError, persisted, backup, upsert, writeBackupFile, exportPayload, protect, auth, locked, currentUser, openData]);

  return createElement(StoreContext.Provider, { value: api }, children);
}

export function useInvoiceStore() {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error("useInvoiceStore must be used inside InvoiceStoreProvider");
  return ctx;
}
